import { Button, FileInput, Radio, RadioGroup, Text } from '@blueprintjs/core'
// TODO - Documentar
import { useState } from 'react'
import { cropComments, parseUploadedFile } from '../../utils/importFunctions'
import * as con from "../../GlobalConstants"


// Style
import "../ComponentsGlobalStyle.css"
import { createNotification } from '../../templates/notifications/Notifications'
import Modal from '../../templates/popups/Modal'
import { downloadInputsTemplate } from '../../utils/reportFunctions'
import { RefModelParameterHook } from '../hooks/ModelParameterHooks'
import { filterObjectByKeys, sum } from '../../GlobalFunctions'
import { translateTransactions } from '../../utils/translateFunctions'
import { StateTransactionsHook } from '../hooks/TransactionsHooks'
import { useDispatch } from 'react-redux'
import { bulkAddTransactions, replaceTransactions } from '../../store/slices/transactionsSlice'


// Constants

const EMPTY_TRANS_DICT = {}

function TransactionsUpload({ onUploadStart, onUploadComplete, disabled }) {

    // Client Type
    const clientType = RefModelParameterHook(con.CLIENT_TYPE)
    
    const dispatch = useDispatch()
    // Upload file
    const [uploadedFile, setUploadedFile] = StateTransactionsHook(con.UPLOADED_FILE)
    const [uploadingFile, setUploadingFile] = StateTransactionsHook(con.UPLOADING_FILE)


    // Options for uploading
    const [exposuresUploadParameter, setExposuresUploadParameter] = useState(() => con.APPEND)
    const [coveragesForwardUploadParameter, setCoveragesForwardUploadParameter] = useState(() => con.APPEND)
    const [coveragesSpotUploadParameter, setCoveragesSpotUploadParameter] = useState(() => con.APPEND)
    const [coveragesOptionUploadParameter, setCoveragesOptionUploadParameter] = useState(() => con.APPEND)
    const [accountsUploadParameter, setAccountsUploadParameter] = useState(() => con.REPLACE)

    // Internal Variables
    const [incorrectRecords, setIncorrectRecords] = useState(() => EMPTY_TRANS_DICT)
    const [correctRecords, setCorrectRecords] = useState(() => EMPTY_TRANS_DICT)
    const [withErrors, setWithErrors] = useState(() => false)

    const handleFileSelect = (e) => {
        if (disabled) return
        setUploadedFile(e.target.files[0])
    }

    const uploadTransactions = async () =>
    {
        onUploadStart?.()
        
        setUploadingFile(true)

        try {
            let [correctTrans, incorrectTrans] = await parseUploadedFile(uploadedFile)
            
            // Filters by used transactions
            let usedTransactions = []
            if(exposuresUploadParameter !== con.IGNORE)
            usedTransactions.push(con.EXPOSURES)
            if(coveragesForwardUploadParameter !== con.IGNORE)
                usedTransactions.push(con.COVERAGES_FWD)
            if(coveragesSpotUploadParameter !== con.IGNORE)
                usedTransactions.push(con.COVERAGES_SPOT)
            if(coveragesOptionUploadParameter !== con.IGNORE)
                usedTransactions.push(con.COVERAGES_OPTION)             
            if(accountsUploadParameter !== con.IGNORE)
                usedTransactions.push(con.ACCOUNTS)           

            // Filters
            incorrectTrans = filterObjectByKeys(incorrectTrans, usedTransactions)
            
            // Checks for Errors
            let transWithErrors = sum(usedTransactions.map( k => incorrectTrans[k].length)) > 0 
            
            // Sets the values
            setIncorrectRecords(incorrectTrans)
            setCorrectRecords(correctTrans)

            setUploadedFile(null)
            setUploadingFile(false)

            setWithErrors(transWithErrors)

            if(!transWithErrors) {
                await dispatchTransactions(correctTrans)
                
                const totalRecords = usedTransactions.reduce((total, transType) => {
                    return total + (correctTrans[transType] ? correctTrans[transType].length : 0)
                }, 0)
                
                onUploadComplete?.(totalRecords)
            } else {
                onUploadComplete?.(0)
            }

        } catch (error) {
            console.error('Error durante la carga:', error)
            setUploadingFile(false)
            createNotification('Error durante la carga del archivo. Por favor, inténtelo nuevamente.')
            onUploadComplete?.(0)
        }
    }

    const dispatchTransactions = async (correctTrans) => {

        const crop = cropComments(correctTrans)

        if (crop) {
            createNotification(
                `El comentario de algunos registros fue recortado. Máximo tamaño admitido: ${con.MAX_COMMENT_LENGTH} caracteres`
            )
        }

        const operations = [
            [exposuresUploadParameter, con.EXPOSURES],
            [coveragesForwardUploadParameter, con.COVERAGES_FWD],
            [coveragesSpotUploadParameter, con.COVERAGES_SPOT],
            [coveragesOptionUploadParameter, con.COVERAGES_OPTION],
            [accountsUploadParameter, con.ACCOUNTS]
        ]

        for (const [parameter, transactionType] of operations) {

            const values = correctTrans[transactionType]

            if (!values || values.length === 0) continue

            // ACCOUNTS no soporta APPEND
            if (transactionType === con.ACCOUNTS && parameter === con.APPEND) {
                continue
            }

            if (parameter === con.APPEND) {

                await dispatch(
                    bulkAddTransactions({
                        transactionType,
                        values
                    })
                ).unwrap()

            } 
            else if (parameter === con.REPLACE) {

                await dispatch(
                    replaceTransactions({
                        transactionType,
                        values
                    })
                ).unwrap()

            }
        }
    }

    const handleErrorUpload = async () => {
        try {
            await dispatchTransactions(correctRecords)
            
            const totalRecords = Object.values(correctRecords).reduce((total, records) => {
                return total + (Array.isArray(records) ? records.length : 0)
            }, 0)
            
            setWithErrors(false)
            onUploadComplete?.(totalRecords)
        } catch (error) {
            console.error('Error durante la carga:', error)
            createNotification('Error durante la carga del archivo. Por favor, inténtelo nuevamente.')
            onUploadComplete?.(0)
        }
    }

    const handleCancel = () => {
        setUploadedFile(null)
        setCorrectRecords(EMPTY_TRANS_DICT)
        setIncorrectRecords(EMPTY_TRANS_DICT)
        setWithErrors(false)
        onUploadComplete?.(0)
    }

    return (        
        <div>            
            <div className="columnParameterBox">                    
                <FileInput 
                    displayname="Buscar" 
                    text={ uploadedFile === null ? "Cargar Archivo..." : uploadedFile.name} 
                    onInputChange={handleFileSelect}
                    disabled={disabled || uploadingFile}
                />
   
                <Button 
                    text="Descargar formato de datos" 
                    className="bp3-minimal" 
                    icon="download" 
                    onClick={(e) => downloadInputsTemplate(clientType)}
                    disabled={disabled || uploadingFile}
                />
            </div>
            <Modal show={uploadedFile != null} doAccept={null} showCancel={false} doCancel={() => setUploadedFile(null)} widthPercentage={70}>      
                <div>
                    <h3>Opciones de Carga</h3>
                    <div className="mediumParameterBox">
                        <RadioGroup label="Exposiciones" onChange={(e) => setExposuresUploadParameter(e.currentTarget.value)} selectedValue={exposuresUploadParameter}>
                            <Radio label="Agregar" value={con.APPEND} disabled={uploadingFile}/>
                            <Radio label="Remplazar" value={con.REPLACE} disabled={uploadingFile}/>
                            <Radio label="Ignorar" value={con.IGNORE} disabled={uploadingFile}/>
                        </RadioGroup>
                        <RadioGroup label="Coberturas FWD" onChange={(e) => setCoveragesForwardUploadParameter(e.currentTarget.value)} selectedValue={coveragesForwardUploadParameter}>
                            <Radio label="Agregar" value={con.APPEND} disabled={uploadingFile}/>
                            <Radio label="Remplazar" value={con.REPLACE} disabled={uploadingFile}/>
                            <Radio label="Ignorar" value={con.IGNORE} disabled={uploadingFile}/>
                        </RadioGroup>  
                        <RadioGroup label="Coberturas Opciones" onChange={(e) => setCoveragesOptionUploadParameter(e.currentTarget.value)} selectedValue={coveragesOptionUploadParameter}>
                            <Radio label="Agregar" value={con.APPEND} disabled={uploadingFile}/>  
                            <Radio label="Remplazar" value={con.REPLACE} disabled={uploadingFile}/>
                            <Radio label="Ignorar" value={con.IGNORE} disabled={uploadingFile}/>
                        </RadioGroup>     
                        <RadioGroup label="Coberturas SPOT" onChange={(e) => setCoveragesSpotUploadParameter(e.currentTarget.value)} selectedValue={coveragesSpotUploadParameter}>
                            <Radio label="Agregar" value={con.APPEND} disabled={uploadingFile}/>
                            <Radio label="Remplazar" value={con.REPLACE} disabled={uploadingFile}/>
                            <Radio label="Ignorar" value={con.IGNORE} disabled={uploadingFile}/>
                        </RadioGroup>     
                        <RadioGroup label="Cuentas" onChange={(e) => setAccountsUploadParameter(e.currentTarget.value)} selectedValue={accountsUploadParameter}>
                            <Radio label="Agregar" value={con.APPEND} disabled/>
                            <Radio label="Remplazar" value={con.REPLACE} disabled={uploadingFile}/>
                            <Radio label="Ignorar" value={con.IGNORE} disabled={uploadingFile}/>
                        </RadioGroup>        
                    </div>
                    <div className="confirmCancelParameterBox">    
                    {
                        uploadingFile ? 
                            <div className="txn-upload-modal-spinner-zx9k">
                                <div className="txn-upload-spinner-wheel-zx9k"></div>
                                <Text className="txn-upload-spinner-text-zx9k">Procesando archivo...</Text>
                            </div> :
                        <div>                                    
                            <Button icon="upload" text="Subir Flujos" onClick={uploadTransactions} />
                            <Button icon="cross" text="Cancelar" onClick={() => setUploadedFile(null)} />
                        </div>
                        }
                    </div>
                </div>
                
            </Modal> 

            <Modal show={withErrors} doAccept={null} showCancel={false} doCancel={handleCancel}>      
                <div>
                    <h3>Errores Detectados</h3>
                    <Text>El archivo recibido contiene los siguientes errores:</Text>
                    <div className="compact" style={{marginLeft : "5%", marginRight : "5%", marginTop : "50px", marginBottom : "50px"}}>
                        {
                            Object.keys(incorrectRecords).map(transactionId =>
                                <ErrorSummary key={transactionId} transactionId={transactionId} errorArr={incorrectRecords[transactionId]} />
                            )                            
                        }
                    </div>
                    <Text>Si lo desea, puede continuar con la importación excluyendo los registros que tienen errores. De lo contrario, cancele la operación, corrija los errores mencionados y vuelva a internarlo. </Text>
                    <div className="confirmCancelParameterBox" style={{marginTop : "15px"}}>    
                    {
                        <div>                                    
                            <Button icon="upload" text="Subir Flujos" onClick={handleErrorUpload} />
                            <Button icon="cross" text="Cancelar" onClick={handleCancel} />
                        </div>
                    }
                    </div>
                </div>
                
            </Modal> 
        </div>
    )
}


function ErrorSummary({transactionId, errorArr}) {

    return(

            errorArr.length > 0 ?
            <div className="compact" style={{textAlign : "left", marginTop : "5px"}}>
                <Text style={{fontWeight: 900}}>{ translateTransactions(transactionId)}</Text>
                {
                    [...Array(Math.min(3,errorArr.length)).keys()].map( i => 
                        <Text key={i} style={{paddingLeft : "10px"}}> - Linea {errorArr[i][con.ROW_NUMBER]}: {con.PARSING_ERROR_MESSAGES[errorArr[i][con.PARSED_ERROR_CODES][0]]} </Text>
                    )
                }
                {
                    errorArr.length > 3
                    ? <Text style={{paddingLeft : "20px"}}>( ... {errorArr.length - 3} error{errorArr.length - 3 > 1 ? "es":""} más.)</Text> 
                    : <></>
                }
            </div>
            : <></>
        
        
    )
}

export default TransactionsUpload